import { IDocument, IFolder } from 'interfaces';

const sortItems = (items: (IDocument | IFolder)[], referenceOrder: string[]) => {
    // Создаем копию массива перед сортировкой
    const itemsCopy = [...items]; // или можно использовать items.slice()
  
    return itemsCopy.sort((a, b) => {
      // 1. Если тип 'folder', сортируем их первыми
      if (a.type === 'folder' && b.type !== 'folder') {
        return -1;
      }
      if (a.type !== 'folder' && b.type === 'folder') {
        return 1;
      }
      
      // 2. Сортировка по referenceOrder для элементов с type != 'folder'
      if (a.type !== 'folder' && b.type !== 'folder') {
        const indexA = referenceOrder.indexOf(a.name);
        const indexB = referenceOrder.indexOf(b.name);
        
        // Если оба элемента есть в referenceOrder, сортируем по порядку
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        
        // Если только один элемент есть в referenceOrder, он идет первым
        if (indexA !== -1) {
          return -1;
        }
        if (indexB !== -1) {
          return 1;
        }
      }
      
      // 3. Если ничего из вышеуказанного, сортируем по алфавиту
      return a.name.localeCompare(b.name);
    });
  };
  
export { sortItems }

const compareByName = (a: IDocument | IFolder, b: IDocument | IFolder): number => {
  if (/^\d/.test(a.name) && /^\d/.test(b.name)) {
    const aData = a.name.match(/(\d+).(\d+).?(\d+)/);
    const bData = b.name.match(/(\d+).(\d+).?(\d+)/);

    if (aData && bData && aData[0] && bData[0]) {
      const dateA = new Date(aData[0].replace(/\./g, '/')).getTime();
      const dateB = new Date(bData[0].replace(/\./g, '/')).getTime();
      const dateDiff = dateA - dateB;
      if (dateDiff !== 0) return dateDiff;
      return a.name.localeCompare(b.name);
    }

    return parseInt(a.name, 10) - parseInt(b.name, 10);
  }
  return a.name.localeCompare(b.name);
};

const compareByUploadAt = (a: IDocument | IFolder, b: IDocument | IFolder): number =>
  ((a as IDocument).upload_at || 0) - ((b as IDocument).upload_at || 0);

const getComparator = (field: string): ((a: IDocument | IFolder, b: IDocument | IFolder) => number) => {
  if (field === 'upload_at') return compareByUploadAt;
  return compareByName;
};

export const sortHierarchical = (
  items: (IDocument | IFolder)[],
  expandedFoldersCache: Record<string, (IDocument | IFolder)[]>,
  field: string,
  order: 'ascend' | 'descend',
): { sortedItems: (IDocument | IFolder)[]; sortedCache: Record<string, (IDocument | IFolder)[]> } => {
  const comparator = getComparator(field);
  const direction = order === 'descend' ? -1 : 1;
  const sorter = (a: IDocument | IFolder, b: IDocument | IFolder) => direction * comparator(a, b);

  const sortedItems = [...items].sort(sorter);

  const sortedCache: Record<string, (IDocument | IFolder)[]> = {};
  for (const [folderId, children] of Object.entries(expandedFoldersCache)) {
    sortedCache[folderId] = [...children].sort(sorter);
  }

  return { sortedItems, sortedCache };
};


export const collectNestedRows = (selectedRows: any, expandedFoldersCache: any) => {
  const uniqueRows = new Map<any, any>();
  
  const queue = [...selectedRows];

  while (queue.length > 0) {
    const row = queue.shift();

    if (row.type !== 'folder') continue;

    const nestedItems = expandedFoldersCache[row.id];
    if (!nestedItems) continue;

    nestedItems.forEach((nestedItem: any) => {
      // Добавляем только уникальные элементы по id
      if (!uniqueRows.has(nestedItem.id)) {
        uniqueRows.set(nestedItem.id, nestedItem);
      }

      // Вложенные папки ставим в очередь
      if (nestedItem.type === 'folder') {
        queue.push(nestedItem);
      }
    });
  }

  return Array.from(uniqueRows.values());
}

export const unselectNestedRows = (
    newSelectedRowKeys: React.Key[],
    selectedRowKeys: React.Key[],
    choosenDocuments: any[],
    currentRows: any[],
  ): { abort: true } | { abort: false; rows: any[] } => {
  let unselectedRow = '';

  if (newSelectedRowKeys.length < selectedRowKeys.length) {
    const setB = new Set(newSelectedRowKeys);
    const unselected = selectedRowKeys.filter(x => !setB.has(x));
    unselectedRow = unselected && unselected[0] ? String(unselected[0]) : '';
  }

  if (!unselectedRow) {
    return { abort: false, rows: currentRows };
  }

  const [type, id, file_id] = unselectedRow.split(' ');
  let rows = currentRows;

  if (type !== 'folder') {
    const targetFile = choosenDocuments.find(el => el.id === file_id);
    if (choosenDocuments.map(el => el.id).includes(String(targetFile?.folder_id))) {
      return { abort: true };
    }
    rows = rows.filter(row => row.id !== targetFile?.id);
    return { abort: false, rows };
  }

  // type === 'folder'
  const targetFolder = choosenDocuments.find(el => el.id === id);
  if (choosenDocuments.map(el => el.id).includes(String(targetFolder?.parent_folder_id))) {
    return { abort: true };
  }

  const removedFolderWithChilds = (folder_id: string) => {
    const removedRows = rows.filter(
      row => row.folder_id === folder_id || row.parent_folder_id === folder_id,
    );
    rows = rows.filter(
      row => row.folder_id !== folder_id && row.parent_folder_id !== folder_id,
    );
    removedRows.forEach(row => {
      if (row.type === 'folder') removedFolderWithChilds(row.id);
    });
  };

  removedFolderWithChilds(id);
  return { abort: false, rows };
}
